import React, { useState, useEffect } from 'react';
import {
  Menu,
  Divider,
  ButtonBase,
  Typography,
  Stack,
  Card,
  CardContent,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  IconButton,
  Alert,
  Box,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { ACCOUNT_STATUS, ACCOUNT_TYPE_LABELS, STATUS_LABELS } from '../constants/accountStatus';
import PageHeader from '../components/common/PageHeader';
import UserDetailsDialog from '../components/UserDetailsDialog';
import { NO_FILTER, QUICK_LABELS, sameFilters, sortUsers, summarize, userMatches } from '../utils/userList';
import { tokens } from '../theme';

const STATUS_STYLE = {
  [ACCOUNT_STATUS.ACTIVE]: { bg: tokens.successSoft, fg: tokens.success },
  [ACCOUNT_STATUS.PENDING_ACTIVATION]: { bg: tokens.warningSoft, fg: tokens.warning },
  [ACCOUNT_STATUS.SUBSCRIPTION_EXPIRED]: { bg: tokens.errorSoft, fg: tokens.error },
  [ACCOUNT_STATUS.BLOCKED]: { bg: tokens.errorSoft, fg: tokens.error },
  default: { bg: tokens.primaryTint, fg: tokens.inkSoft },
};

const accessLabel = (user) => {
  if (user?.status === ACCOUNT_STATUS.PENDING_ACTIVATION) return 'Дать доступ';
  if (user?.status === ACCOUNT_STATUS.SUBSCRIPTION_EXPIRED) return 'Продлить доступ';
  return 'Разблокировать';
};

const accessTerm = (user) => {
  if (!user.activated_at) return 'Доступ не выдан';
  if (!user.expires_at) return 'Без ограничения срока';
  const days = Math.ceil((new Date(user.expires_at) - Date.now()) / 86400000);
  return days > 0 ? `Осталось дней: ${days}` : 'Срок доступа истёк';
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [verifyingId, setVerifyingId] = useState(null);
  const [verificationNotice, setVerificationNotice] = useState(null);
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(NO_FILTER); // status, type, activity (active / inactive), quick slice
  const [sort, setSort] = useState({ key: 'created', dir: 'desc' });
  const [detailsId, setDetailsId] = useState(null); // whose card is open
  const [menu, setMenu] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'user',
    valid_days: '',
    trial: false,
  });
  const [error, setError] = useState('');
  const [unblockDialog, setUnblockDialog] = useState({ open: false, user: null, days: '30', unlimited: false, error: '' });
  const [editDialog, setEditDialog] = useState({ open: false, user: null, email: '', role: 'user', valid_days: '', trial: false, type: 'auto' });
  const [editError, setEditError] = useState('');
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null, password: '', error: '' });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data || []);
    } catch (error) {
      setVerificationNotice({ severity: 'error', message: 'Не удалось загрузить пользователей. Обновите страницу.' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setError('');
    setFormData({ email: '', password: '', role: 'user', valid_days: '', trial: false });
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
    setFormData({ email: '', password: '', role: 'user', valid_days: '', trial: false });
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const payload = {
        email: formData.email,
        password: formData.password,
        role: formData.role,
        trial: formData.trial,
      };
      if (formData.valid_days !== '' && formData.valid_days != null) {
        const days = parseInt(formData.valid_days, 10);
        if (!isNaN(days) && days > 0) payload.valid_days = days;
      }
      await api.post('/admin/users', payload);
      handleClose();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка при создании пользователя');
    }
  };

  const handleVerifyEmail = async (user) => {
    if (verifyingId) return;
    setVerifyingId(user.id);
    setVerificationNotice(null);
    try {
      await api.patch(`/admin/users/${user.id}/verify-email`);
      await loadUsers();
      setVerificationNotice({ severity: 'success', message: `Почта ${user.email} подтверждена.` });
    } catch (err) {
      setVerificationNotice({ severity: 'error', message: 'Не удалось подтвердить почту. Попробуйте ещё раз.' });
    } finally {
      setVerifyingId(null);
    }
  };

  const handleUnblockClick = (user) => {
    setUnblockDialog({ open: true, user, days: '30', unlimited: false, error: '' });
  };

  const handleUnblockClose = () => {
    setUnblockDialog({ open: false, user: null, days: '30', unlimited: false, error: '' });
  };

  const handleUnblock = async () => {
    const { user, days, unlimited } = unblockDialog;
    if (!user || saving) return;
    if (!unlimited && (!Number.isInteger(Number(days)) || Number(days) < 1 || Number(days) > 36500)) {
      setUnblockDialog((prev) => ({ ...prev, error: 'Введите целое число дней от 1 до 36500.' }));
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}/unblock`, { days: unlimited ? 0 : Number(days) });
      handleUnblockClose();
      await loadUsers();
    } catch (error) {
      setUnblockDialog((prev) => ({ ...prev, error: 'Не удалось выдать доступ. Попробуйте ещё раз.' }));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirm || saving || confirm.user.id === currentUser?.id) return;
    setSaving(true);
    try {
      if (confirm.action === 'delete') await api.delete(`/admin/users/${confirm.user.id}`);
      else if (confirm.action === 'revoke') await api.post(`/admin/users/${confirm.user.id}/revoke-access`);
      else await api.patch(`/admin/users/${confirm.user.id}/block`);
      setConfirm(null);
      await loadUsers();
    } catch (error) {
      setConfirm((prev) => ({ ...prev, error: 'Не удалось выполнить действие. Попробуйте ещё раз.' }));
    } finally {
      setSaving(false);
    }
  };

  const menuAction = (action) => {
    const user = menu.user;
    setMenu(null);
    action(user);
  };

  const handleEditOpen = (user, mode = 'settings') => {
    setEditDialog({
      open: true,
      mode,
      user,
      email: user.email,
      role: user.role,
      valid_days: '', // пусто = не менять срок
      trial: !!user.is_trial,
      type: user.type_manual ? user.account_type : 'auto', // auto = worked out from the data
    });
    setEditError('');
  };

  const handleEditClose = () => {
    setEditDialog({ open: false, user: null, email: '', role: 'user', valid_days: '', trial: false, type: 'auto' });
    setEditError('');
  };

  const handleEditSubmit = async () => {
    const { user } = editDialog;
    if (!user) return;
    try {
      setEditError('');
      const payload = editDialog.mode === 'email' ? { email: editDialog.email.trim() } : { role: editDialog.role, trial: editDialog.trial, type: editDialog.type };
      if (editDialog.valid_days !== '') {
        const d = parseInt(editDialog.valid_days, 10);
        payload.valid_days = isNaN(d) ? 0 : d;
      }
      await api.patch(`/admin/users/${user.id}`, payload);
      handleEditClose();
      loadUsers();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Ошибка при сохранении');
    }
  };

  const handlePasswordOpen = (user) => {
    setPasswordDialog({ open: true, user, password: '', error: '' });
  };

  const handlePasswordClose = () => {
    setPasswordDialog({ open: false, user: null, password: '', error: '' });
  };

  const handlePasswordSubmit = async () => {
    const { user, password } = passwordDialog;
    if (!user || !password.trim()) {
      setPasswordDialog((prev) => ({ ...prev, error: 'Введите новый пароль' }));
      return;
    }
    try {
      await api.patch(`/admin/users/${user.id}/password`, { password: password.trim() });
      handlePasswordClose();
    } catch (err) {
      setPasswordDialog((prev) => ({ ...prev, error: err.response?.data?.error || 'Ошибка при смене пароля' }));
    }
  };

  const summary = summarize(users);
  const visibleUsers = sortUsers(users.filter((u) => userMatches(u, { ...filters, search })), sort.key, sort.dir);
  const filtered = !sameFilters(filters, NO_FILTER) || search.trim() !== '';
  const resetFilters = () => { setSearch(''); setFilters(NO_FILTER); };
  const sortBy = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'email' || key === 'type' || key === 'status' ? 'asc' : 'desc' }));
  const sortHead = (key, label) => (
    <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'asc'} onClick={() => sortBy(key)}>{label}</TableSortLabel>
  );
  const TONE = { success: tokens.success, warning: tokens.warning, error: tokens.error };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <PageHeader overline="Администрирование" title="Пользователи" subtitle="Аккаунты, доступ и сроки подписок"
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>Создать пользователя</Button>} />

      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '5fr 8fr 4fr' }, columnGap: 3.5, rowGap: 2.5 }}>
            {summary.groups.map((group) => (
              <Box key={group.title} sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.inkMuted }}>{group.title}</Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: tokens.border }} />
                </Stack>
                <Box sx={{ display: 'grid', gap: 1,
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))', xl: `repeat(${group.tiles.length}, minmax(0, 1fr))` } }}>
                  {group.tiles.map((tile) => {
                    const active = sameFilters(filters, tile.filter);
                    return (
                      <ButtonBase key={tile.key} onClick={() => setFilters(tile.filter)} aria-pressed={active} title={tile.hint}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', textAlign: 'left', gap: 0.5,
                          minHeight: 70, px: 1.5, py: 1.25, borderRadius: '14px', border: 'none',
                          bgcolor: active ? tokens.primarySoft : tokens.bg,
                          boxShadow: active ? `inset 0 0 0 1.5px ${tokens.primary}` : 'none',
                          transition: 'background-color .15s, box-shadow .15s, transform .15s',
                          '&:hover': { boxShadow: `inset 0 0 0 1.5px ${active ? tokens.primary : tokens.borderStrong}`, transform: 'translateY(-1px)' } }}>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em', color: TONE[tile.tone] || tokens.ink }}>{tile.value}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.25, color: tokens.inkSoft, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{tile.label}</Typography>
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
          <Typography sx={{ mt: 1.75, fontSize: '0.75rem', color: tokens.inkMuted }}>
            Цифры без администраторов ({summary.admins}). Нажмите на плитку, чтобы отфильтровать список.
          </Typography>
        </CardContent>
      </Card>

      {verificationNotice && (
        <Alert severity={verificationNotice.severity} onClose={() => setVerificationNotice(null)} sx={{ mb: 2 }}>
          {verificationNotice.message}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ pb: '8px !important' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <TextField placeholder="Поиск по email" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220, maxWidth: 420 }}
              inputProps={{ 'aria-label': 'Поиск по email' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }} />
            <ToggleButtonGroup exclusive size="small" value={filters.activity} onChange={(_, v) => v && setFilters({ ...filters, activity: v })} aria-label="Активность">
              <ToggleButton value="all">Все</ToggleButton>
              <ToggleButton value="active">Активные</ToggleButton>
              <ToggleButton value="inactive">Неактивные</ToggleButton>
            </ToggleButtonGroup>
            <TextField select size="small" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} sx={{ minWidth: 190 }} inputProps={{ 'aria-label': 'Статус' }}>
              <MenuItem value="all">Любой статус</MenuItem>
              {Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField select size="small" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} sx={{ minWidth: 170 }} inputProps={{ 'aria-label': 'Тип' }}>
              <MenuItem value="all">Любой тип</MenuItem>
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField select size="small" value={filters.quick} onChange={(e) => setFilters({ ...filters, quick: e.target.value })} sx={{ minWidth: 230 }} inputProps={{ 'aria-label': 'Срез' }}>
              {Object.entries(QUICK_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            {filtered && <Button size="small" onClick={resetFilters}>Сбросить</Button>}
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">Найдено: {visibleUsers.length}</Typography>
          </Stack>

          <TableContainer sx={{ border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{sortHead('email', 'Пользователь')}</TableCell>
                  <TableCell>{sortHead('type', 'Тип')}</TableCell>
                  <TableCell>{sortHead('status', 'Статус')}</TableCell>
                  <TableCell>{sortHead('expires', 'Срок доступа')}</TableCell>
                  <TableCell>{sortHead('created', 'Создан')}</TableCell>
                  <TableCell>{sortHead('login', 'Последний вход')}</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleUsers.map((user) => {
                  const st = STATUS_STYLE[user.status] || STATUS_STYLE.default;
                  return (
                    <TableRow key={user.id} hover onClick={() => setDetailsId(user.id)} sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                            bgcolor: user.role === 'admin' ? tokens.profile : tokens.primarySoft, color: user.role === 'admin' ? '#7FE3DC' : tokens.primaryDark }}>
                            {user.email.slice(0, 1).toUpperCase()}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{user.email}</Typography>
                            {user.id === currentUser?.id && <Typography variant="caption" color="text.secondary">Вы</Typography>}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.role === 'admin' ? 'Администратор' : (ACCOUNT_TYPE_LABELS[user.account_type] || 'Пользователь')}</Typography>
                        {user.role !== 'admin' && user.type_manual && <Typography variant="caption" color="text.secondary">задан вручную</Typography>}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                          <Chip size="small" label={STATUS_LABELS[user.status] || user.status} sx={{ bgcolor: st.bg, color: st.fg }} />
                          {user.is_trial && <Chip size="small" label="Пробный" sx={{ bgcolor: tokens.infoSoft, color: tokens.info }} />}
                          {user.trial_lapsed && <Chip size="small" label="Пробный закончился, не купил" sx={{ bgcolor: tokens.warningSoft, color: tokens.warning }} />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: user.status === ACCOUNT_STATUS.SUBSCRIPTION_EXPIRED ? tokens.error : tokens.ink }}>{accessTerm(user)}</Typography>
                        {user.activated_at && user.expires_at && <Typography variant="caption" color="text.secondary">до {new Date(user.expires_at).toLocaleDateString('ru-RU')}</Typography>}
                      </TableCell>
                      <TableCell>{new Date(user.created_at).toLocaleDateString('ru-RU')}</TableCell>
                      <TableCell>{user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('ru-RU') : <Typography variant="body2" color="text.secondary">—</Typography>}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.75}>
                          {!user.email_verified_at && (
                            <Button size="small" variant="outlined" disabled={verifyingId !== null} onClick={() => handleVerifyEmail(user)}
                              title="Подтвердить почту без изменения доступа и срока подписки" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {verifyingId === user.id ? 'Подтверждение…' : 'Подтвердить почту'}
                            </Button>
                          )}
                          <IconButton aria-label={`Действия для ${user.email}`} aria-haspopup="menu" aria-expanded={menu?.user.id === user.id ? true : undefined}
                            onClick={(event) => setMenu({ anchor: event.currentTarget, user })}>
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {visibleUsers.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography>Пользователи не найдены</Typography>
                  <Button onClick={resetFilters}>Сбросить фильтры</Button>
                </TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => menuAction((user) => setDetailsId(user.id))}>Открыть карточку</MenuItem>
        <MenuItem disabled={menu?.user.role === 'admin'} onClick={() => menuAction(handleUnblockClick)}>{accessLabel(menu?.user)}</MenuItem>
        <MenuItem sx={{ color: 'error.main' }} disabled={!menu?.user.activated_at || menu?.user.role === 'admin' || menu?.user.id === currentUser?.id}
          onClick={() => menuAction((user) => setConfirm({ user, action: 'revoke' }))}>Забрать доступ</MenuItem>
        <Divider />
        <MenuItem onClick={() => menuAction((user) => handleEditOpen(user, 'email'))}>Изменить email</MenuItem>
        <MenuItem onClick={() => menuAction(handlePasswordOpen)}>Сменить пароль</MenuItem>
        <MenuItem onClick={() => menuAction(handleEditOpen)}>Настроить доступ и роль</MenuItem>
        <Divider />
        <MenuItem disabled={!menu?.user.is_active || menu?.user.id === currentUser?.id}
          onClick={() => menuAction((user) => setConfirm({ user, action: 'block' }))}>Заблокировать</MenuItem>
        <MenuItem sx={{ color: 'error.main' }} disabled={menu?.user.id === currentUser?.id}
          onClick={() => menuAction((user) => setConfirm({ user, action: 'delete' }))}>Удалить пользователя</MenuItem>
      </Menu>
      <Dialog open={Boolean(confirm)} onClose={() => !saving && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{{ delete: 'Удалить пользователя?', revoke: 'Забрать доступ?' }[confirm?.action] || 'Заблокировать пользователя?'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ overflowWrap: 'anywhere', mb: 2 }}>{confirm?.user.email}</Typography>
          <Typography color="text.secondary">{{
            delete: 'Аккаунт будет удалён. Отменить это действие нельзя.',
            revoke: 'Срок доступа обнулится, но пользователь не блокируется: он сможет войти, увидит «Тарифы» и «Настройки», а разделы сервиса закроются до выдачи доступа или оплаты. Бесплатный пробный период ему повторно не предлагается. Действие попадёт в журнал.',
          }[confirm?.action] || 'Пользователь потеряет доступ к сервису. Вы сможете разблокировать его позже.'}</Typography>
          {confirm?.error && <Alert severity="error" sx={{ mt: 2 }}>{confirm.error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setConfirm(null)}>Отмена</Button>
          <Button disabled={saving} color="error" variant="contained" onClick={handleConfirm}>{saving ? 'Сохранение…' : { delete: 'Удалить', revoke: 'Забрать доступ' }[confirm?.action] || 'Заблокировать'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Создать пользователя</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Пароль"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Роль</InputLabel>
            <Select
              value={formData.role}
              label="Роль"
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <MenuItem value="user">Пользователь</MenuItem>
              <MenuItem value="admin">Администратор</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Срок действия (дней)"
            type="number"
            inputProps={{ min: 1 }}
            placeholder="Пусто = без срока"
            value={formData.valid_days}
            onChange={(e) => setFormData({ ...formData, valid_days: e.target.value })}
            margin="normal"
            helperText="Оставьте пустым для аккаунта без ограничения по сроку"
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={formData.trial}
                onChange={(e) => setFormData({ ...formData, trial: e.target.checked })}
              />
            }
            label="Пробный период (без скачивания отчётов)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained">
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialog.open} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editDialog.mode === 'email' ? 'Изменить email' : 'Настроить доступ и роль'}</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          {editDialog.mode === 'email' && <TextField
            fullWidth
            label="Email"
            type="email"
            value={editDialog.email}
            onChange={(e) => setEditDialog({ ...editDialog, email: e.target.value })}
            margin="normal"
            required
          />}
          {editDialog.mode !== 'email' && <>
          <FormControl fullWidth margin="normal">
            <InputLabel>Роль</InputLabel>
            <Select
              value={editDialog.role}
              label="Роль"
              onChange={(e) => setEditDialog({ ...editDialog, role: e.target.value })}
            >
              <MenuItem value="user">Пользователь</MenuItem>
              <MenuItem value="admin">Администратор</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Тип</InputLabel>
            <Select
              value={editDialog.type}
              label="Тип"
              onChange={(e) => setEditDialog({ ...editDialog, type: e.target.value })}
            >
              <MenuItem value="auto">Определять автоматически</MenuItem>
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
            <FormHelperText>Только метка для списка: доступ задаётся сроком и пробным периодом.</FormHelperText>
          </FormControl>
          <TextField
            fullWidth
            label="Срок действия (дней)"
            type="number"
            inputProps={{ min: 0 }}
            placeholder="Пусто = не менять"
            value={editDialog.valid_days}
            onChange={(e) => setEditDialog({ ...editDialog, valid_days: e.target.value })}
            margin="normal"
            helperText="Пусто — не менять. 0 — без срока. Число — установить срок с сегодня на N дней."
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={editDialog.trial}
                onChange={(e) => setEditDialog({ ...editDialog, trial: e.target.checked })}
              />
            }
            label="Пробный период (без скачивания отчётов)"
          />
          </>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Отмена</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialog.open} onClose={handlePasswordClose} maxWidth="xs" fullWidth>
        <DialogTitle>Смена пароля</DialogTitle>
        <DialogContent>
          {passwordDialog.user && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Пользователь: {passwordDialog.user.email}
            </Typography>
          )}
          {passwordDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordDialog.error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Новый пароль"
            type="password"
            autoComplete="new-password"
            value={passwordDialog.password}
            onChange={(e) =>
              setPasswordDialog((prev) => ({ ...prev, password: e.target.value, error: '' }))
            }
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordClose}>Отмена</Button>
          <Button onClick={handlePasswordSubmit} variant="contained">
            Сохранить пароль
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unblockDialog.open} onClose={() => !saving && handleUnblockClose()} maxWidth="xs" fullWidth>
        <DialogTitle>{accessLabel(unblockDialog.user)}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2, overflowWrap: 'anywhere' }}>{unblockDialog.user?.email}</Typography>
          {unblockDialog.error && <Alert severity="error" sx={{ mb: 2 }}>{unblockDialog.error}</Alert>}
          <FormControlLabel control={<Switch checked={unblockDialog.unlimited} disabled={saving}
            onChange={(e) => setUnblockDialog({ ...unblockDialog, unlimited: e.target.checked })} />} label="Без ограничения срока" />
          {!unblockDialog.unlimited && <TextField fullWidth label="Срок доступа (дней)" type="number" disabled={saving}
            inputProps={{ min: 1, max: 36500, step: 1 }} value={unblockDialog.days}
            onChange={(e) => setUnblockDialog({ ...unblockDialog, days: e.target.value })} margin="normal"
            helperText="Срок отсчитывается с сегодняшнего дня." />}
          <Typography variant="body2" sx={{ mt: 2 }}>
            {unblockDialog.unlimited ? 'Доступ будет предоставлен без ограничения срока.' : Number.isInteger(Number(unblockDialog.days)) && Number(unblockDialog.days) > 0 && Number(unblockDialog.days) <= 36500
              ? `Доступ до ${new Date(Date.now() + Number(unblockDialog.days) * 86400000).toLocaleDateString('ru-RU')}` : 'Укажите срок доступа.'}
          </Typography>
          {unblockDialog.user?.is_trial && <Alert severity="info" sx={{ mt: 2 }}>Пробный период завершится. Будет выдан полный доступ, включая скачивание отчётов.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={handleUnblockClose}>Отмена</Button>
          <Button disabled={saving} onClick={handleUnblock} variant="contained">
            {saving ? 'Сохранение…' : unblockDialog.user?.status === ACCOUNT_STATUS.PENDING_ACTIVATION ? 'Активировать' : accessLabel(unblockDialog.user)}
          </Button>
        </DialogActions>
      </Dialog>
      {detailsId && <UserDetailsDialog userId={detailsId} onClose={() => setDetailsId(null)} />}
    </Box>
  );
};

export default AdminUsersPage;


